import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  Renderer2,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { RouterLink } from '@angular/router';
import {
  AsYouType,
  isValidPhoneNumber,
  parsePhoneNumberWithError,
} from 'libphonenumber-js';
import { catchError, of, take, throwError, timer } from 'rxjs';

import { AuthService } from 'src/app/core/services/auth';
import { SharedModule } from 'src/app/shared/shared.module';

function phoneValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null;
    }

    return isValidPhoneNumber(control.value as string, 'US')
      ? null
      : { phone: true };
  };
}

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, SharedModule, RouterLink],
})
export class RegisterComponent implements AfterViewInit {
  isLoading = signal(false);

  submitted = signal(false);

  registrationComplete = signal(false);

  errorMessage = signal('');

  registerFormGroup: FormGroup<{
    identifier: FormControl<string>;
    email: FormControl<string>;
    firstName: FormControl<string>;
    lastName: FormControl<string>;
    phone: FormControl<string>;
    jobTitle: FormControl<string>;
    termsAccepted: FormControl<boolean>;
  }> = new FormGroup({
    identifier: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(4),
        Validators.pattern(/^\d+$/),
      ],
    }),
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    firstName: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    lastName: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    phone: new FormControl('', {
      nonNullable: true,
      validators: [phoneValidator()],
    }),
    jobTitle: new FormControl('', { nonNullable: true }),
    termsAccepted: new FormControl(false, {
      nonNullable: true,
      validators: [Validators.requiredTrue],
    }),
  });

  private authService = inject(AuthService);

  private renderer = inject(Renderer2);

  ngAfterViewInit(): void {
    this.renderer.selectRootElement('#identifierId').focus();
  }

  onIdentifierKeydown(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey || event.key.length > 1) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onIdentifierPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const digitsOnly = (event.clipboardData?.getData('text') ?? '').replace(
      /\D/g,
      '',
    );
    this.registerFormGroup.controls.identifier.setValue(digitsOnly);
  }

  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 10);
    const formatted = new AsYouType('US').input(digits);
    input.value = formatted;
    this.registerFormGroup.controls.phone.setValue(formatted);
  }

  register(): void {
    this.submitted.set(true);
    this.errorMessage.set('');

    if (this.registerFormGroup.invalid) {
      this.registerFormGroup.markAllAsTouched();
      return;
    }

    const {
      identifier,
      email,
      firstName,
      lastName,
      phone,
      jobTitle,
    } = this.registerFormGroup.getRawValue();
    this.isLoading.set(true);

    this.authService
      .register({
        identifier: identifier.trim(),
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim()
          ? parsePhoneNumberWithError(phone.trim(), 'US').number
          : undefined,
        jobTitle: jobTitle.trim() || undefined,
        termsAcceptedAt: new Date().toISOString(),
      })
      .pipe(
        take(1),
        catchError((error: HttpErrorResponse) => {
          // Keep any server-evaluated response generic to avoid account
          // enumeration; only a failed/unreachable request gets its own message.
          if (error.status === 0 || error.status >= 500) {
            return throwError(() => error);
          }
          return of(null);
        }),
      )
      .subscribe({
        next: () => {
          this.isLoading.set(false);
          this.registrationComplete.set(true);
        },
        error: () => {
          this.isLoading.set(false);
          this.errorMessage.set(
            "We couldn't process your request. Please try again.",
          );
          this.clearErrorMessage();
        },
      });
  }

  private clearErrorMessage(): void {
    timer(5000)
      .pipe(take(1))
      .subscribe(() => {
        this.errorMessage.set('');
      });
  }
}

